import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Ellipsis, FileUp, Search, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { pluralise } from '@/shared/lib/format';
import type { Subscriber, SubscriberListRef } from '../api/subscribersApi';
import { useSubscribersQuery } from '../hooks/useSubscriberQueries';
import { MemberRow } from './MemberRow';
import { enterSx, outlineButtonSx, panelSx, tagSx } from './styles';

const PAGE_SIZE = 20;

export type PanelActions = {
  onAdd: () => void;
  onImport: () => void;
  onRename: () => void;
  onDelete: () => void;
  onRemove: (s: Subscriber) => void;
  removingId: string | null;
};

function MoreMenu({ onRename, onDelete }: Pick<PanelActions, 'onRename' | 'onDelete'>) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const pick = (fn: () => void) => () => { setAnchor(null); fn(); };
  return (
    <>
      <IconButton size="small" aria-label="More list actions" onClick={(e) => { setAnchor(e.currentTarget); }} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Ellipsis size={16} />
      </IconButton>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => { setAnchor(null); }} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={pick(onRename)}>Rename or describe</MenuItem>
        <MenuItem onClick={pick(onDelete)} sx={{ color: 'error.main' }}>Delete list</MenuItem>
      </Menu>
    </>
  );
}

function Header({ list, q, onQ, actions }: { list: SubscriberListRef; q: string; onQ: (q: string) => void; actions: PanelActions }) {
  const owner = list.kind === 'topic' ? 'Global Markets Research' : (list.ownerName ?? 'an RM');
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 1.5, alignItems: { md: 'flex-start' }, p: { xs: 2, md: 2.5 }, borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ flexGrow: 1, minWidth: { xs: 0, md: 220 } }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography id="list-title" variant="h3" component="h2">{list.name}</Typography>
          <Box component="span" sx={tagSx}>{list.kind === 'topic' ? 'Topic list' : 'Client book'}</Box>
        </Stack>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Owned by {owner} · {pluralise(list.count, 'number')}{list.optedInCount < list.count ? ` · ${list.optedInCount} opted in` : ''}
        </Typography>
      </Box>
      {list.editable ? (
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: { xs: 'wrap', lg: 'nowrap' }, flexShrink: 0 }}>
          <TextField
            size="small"
            type="search"
            placeholder="Search this list"
            value={q}
            onChange={(e) => { onQ(e.target.value); }}
            slotProps={{ htmlInput: { 'aria-label': 'Search this list' }, input: { startAdornment: <InputAdornment position="start"><Search size={14} aria-hidden /></InputAdornment> } }}
            sx={{ width: { xs: '100%', sm: 170 }, '& .MuiOutlinedInput-root': { fontSize: '0.8125rem' } }}
          />
          <Button variant="outlined" startIcon={<UserPlus size={15} />} onClick={actions.onAdd} sx={outlineButtonSx}>Add subscriber</Button>
          <Button variant="outlined" startIcon={<FileUp size={15} />} onClick={actions.onImport} sx={outlineButtonSx}>Import CSV</Button>
          <MoreMenu onRename={actions.onRename} onDelete={actions.onDelete} />
        </Stack>
      ) : null}
    </Stack>
  );
}

function Pager({ page, total, onPage }: { page: number; total: number; onPage: (p: number) => void }) {
  const last = Math.min(page * PAGE_SIZE, total);
  return (
    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'flex-end', gap: 1, px: 2.5, py: 1 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{(page - 1) * PAGE_SIZE + 1}–{last} of {total}</Typography>
      <Button size="small" disabled={page === 1} onClick={() => { onPage(page - 1); }}>Previous</Button>
      <Button size="small" disabled={last >= total} onClick={() => { onPage(page + 1); }}>Next</Button>
    </Stack>
  );
}

function Members({ list, q, actions }: { list: SubscriberListRef; q: string; actions: PanelActions }) {
  const [page, setPage] = useState(1);
  const [confirming, setConfirming] = useState<string | null>(null);
  const members = useSubscribersQuery({ page, pageSize: PAGE_SIZE, listId: list.id, q });
  const note = (text: string) => <Typography variant="body2" sx={{ color: 'text.secondary', p: 2.5 }}>{text}</Typography>;

  if (members.isPending) return <Stack sx={{ p: 2.5, gap: 1.5 }}>{[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={44} />)}</Stack>;
  if (members.isError) return note('Could not load this list. Try again.');
  if (members.data.total === 0) return note(q ? 'Nobody on this list matches.' : 'Nobody on this list yet. Add a subscriber or import a CSV.');
  return (
    <>
      <Box component="ul" aria-label={`People on ${list.name}`} sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {members.data.items.map((s) => (
          <MemberRow
            key={s.id}
            s={s}
            canEdit={list.editable}
            confirming={confirming === s.id}
            removing={actions.removingId === s.id}
            onAsk={() => { setConfirming(s.id); }}
            onKeep={() => { setConfirming(null); }}
            onRemove={() => { setConfirming(null); actions.onRemove(s); }}
          />
        ))}
      </Box>
      {members.data.total > PAGE_SIZE ? <Pager page={page} total={members.data.total} onPage={setPage} /> : null}
    </>
  );
}

/** One list: who owns it, its tools, and the people on it. Lists this account cannot manage show only their totals. */
export function ListPanel({ list, actions }: { list: SubscriberListRef; actions: PanelActions }) {
  const [q, setQ] = useState('');
  const search = useDebouncedValue(q.trim());
  return (
    <Box component="section" aria-labelledby="list-title" sx={{ ...panelSx, ...enterSx }}>
      <Header list={list} q={q} onQ={setQ} actions={actions} />
      {list.editable ? (
        <Members key={`${list.id}-${search}`} list={list} q={search} actions={actions} />
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary', p: 2.5 }}>
          Only the RM who owns this list, and admins, can see who is on it.
        </Typography>
      )}
    </Box>
  );
}
